import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallRouteV1Service, serviceName } from '../service';
import { UsageV2IO, ServiceQueries } from '../types';
import { useCallRouteV1Queries } from './use-call-route-v1-queries';
import { UseCallRouteV1QueryEndpointArgs, useCallRouteV1Query } from './use-call-route-v1-query';

/**
 * A hook that returns a query for the `UsageV2` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useUsageV2Query = <E = unknown, D = UsageV2IO['output']>(
  args: UseCallRouteV1QueryEndpointArgs<'UsageV2', E, D>
) =>
  useCallRouteV1Query({
    endpointName: 'UsageV2',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `UsageV2` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useUsageV2Queries = <E = unknown, D = UsageV2IO['output']>(
  argsArr: UseCallRouteV1QueryEndpointArgs<'UsageV2', E, D>[]
) =>
  useCallRouteV1Queries(
    argsArr.map((args) => ({
      endpointName: 'UsageV2',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `UsageV2` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchUsageV2 = () => {
  const queryClient = useQueryClient();

  const fetchUsageV2 = async <E = unknown, D = UsageV2IO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallRouteV1QueryEndpointArgs<'UsageV2', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      UsageV2IO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'UsageV2'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        UsageV2IO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'UsageV2'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'UsageV2'>({
          serviceName,
          endpointName: 'UsageV2',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallRouteV1Service.UsageV2(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchUsageV2;
};
